import axios, { AxiosPromise } from "axios";
import { action, computed, makeObservable, observable, runInAction } from "mobx";

export interface RequestStatus {
  httpStatus: number;
  isSent: boolean;
  isPending: boolean;
  isDone: boolean;
  isError: boolean;
  isCancelled: boolean;
  partnerApiErrorCodes: string[];
  isProxiesExceeded: boolean;

  reset(): void;

  handleRequest<TResponse>(fetchMethod: () => AxiosPromise<TResponse>, returnAllResponse?: boolean):
    Promise<TResponse | null>;
}

export class DefaultRequestStatus implements RequestStatus {
  constructor() {
    makeObservable(this);
  }

  @observable
  private _httpStatus: number = 0;

  @observable
  private _isSent: boolean = false;

  @observable
  private _isPending: boolean = false;

  @observable
  private _isDone: boolean = false;

  @observable
  private _isCancelled: boolean = false;

  @observable
  private _partnerApiErrorCodes: string[] = [];

  @computed
  public get httpStatus(): number {
    return this._httpStatus;
  }

  @computed
  public get isSent(): boolean {
    return this._isSent;
  }

  @computed
  public get isPending(): boolean {
    return this._isPending;
  }

  @computed
  public get isDone(): boolean {
    return this._isDone;
  }

  @computed
  public get isError(): boolean {
    return this._httpStatus >= 400;
  }

  @computed
  public get isCancelled(): boolean {
    return this._isCancelled;
  }

  @computed
  public get partnerApiErrorCodes(): string[] {
    return this._partnerApiErrorCodes;
  }

  @action
  public reset() {
    this._httpStatus = 0;
    this._isSent = false;
    this._isPending = false;
    this._isDone = false;
    this._isCancelled = false;
    this._partnerApiErrorCodes = [];
  }

  @computed
  public get isProxiesExceeded(): boolean {
    return this.isError && this.partnerApiErrorCodes && this.partnerApiErrorCodes[0] === "proxiesExceeded";
  }

  public async handleRequest<TResponse>(fetchMethod: () => AxiosPromise<TResponse>, returnAllResponse?: boolean):
    Promise<TResponse> {
    // @ts-ignore
    return this.handle<TResponse>(async () => {
      const response = await fetchMethod();
      if (returnAllResponse) {
        return response;
      }
      return response.data;
    });
  }

  private async handle<T>(method: () => Promise<T>) {
    try {
      runInAction(() => {
        this._isSent = true;
        this._isPending = true;
        this._isDone = false;
        this._isCancelled = false;
        this._httpStatus = 0;
      });

      return await method();
    } catch (err) {
      // axios.isCancel would be true only if we canceled the request intentionally, we don't treat it as an error
      if (!axios.isCancel(err)) {
        this._isCancelled = false;

        runInAction(() => {
          if (err.response) {
            this._httpStatus = err.response.status;
            this._partnerApiErrorCodes = err.response.data && err.response.data.partnerApiErrorCodes &&
              err.response.data.partnerApiErrorCodes;
          } else if (err.request) {
            this._httpStatus = err.request.status;
          } else {
            this._httpStatus = 500;
          }
        });
      } else {
        this._isCancelled = true;
      }

      return null;
    } finally {
      runInAction(() => {
        this._isPending = false;
        this._isDone = true;
      });
    }
  }
}
